import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

function siteOrigin() {
  const fromEnv = (process.env.REACT_APP_SITE_URL || '').trim().replace(/\/+$/, '');
  if (typeof window !== 'undefined' && !fromEnv) return window.location.origin;
  return fromEnv;
}

/**
 * Route-level SEO without react-helmet-async (React 19 peer-deps).
 * Updates document title, description meta, and canonical link while mounted; restores title on unmount.
 */
export default function PageSeo({
  documentTitle,
  description,
  keywords,
  canonicalPath,
  noindex,
}) {
  const location = useLocation();
  const prevTitle = useRef(typeof document !== 'undefined' ? document.title : '');

  useEffect(() => {
    const t =
      documentTitle != null && String(documentTitle).trim()
        ? String(documentTitle).trim()
        : 'Moda MAI';
    prevTitle.current = document.title;
    document.title = t;
    return () => {
      document.title = prevTitle.current || 'Moda MAI';
    };
  }, [documentTitle]);

  useEffect(() => {
    const desc = description != null && String(description).trim() ? String(description).trim().slice(0, 320) : '';
    let el = document.querySelector('meta[name="description"]');
    const had = el?.getAttribute('content') ?? '';
    if (desc) {
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', 'description');
        document.head.appendChild(el);
      }
      el.setAttribute('content', desc);
    }
    return () => {
      if (el && had) el.setAttribute('content', had);
    };
  }, [description]);

  useEffect(() => {
    const kw = keywords != null && String(keywords).trim() ? String(keywords).trim().slice(0, 500) : '';
    if (!kw) return undefined;
    let el = document.querySelector('meta[name="keywords"]');
    const had = el?.getAttribute('content') ?? '';
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'keywords');
      document.head.appendChild(el);
    }
    el.setAttribute('content', kw);
    return () => {
      if (el) {
        if (had) el.setAttribute('content', had);
        else el.remove();
      }
    };
  }, [keywords]);

  useEffect(() => {
    const origin = siteOrigin();
    if (document.querySelector('link[rel="canonical"][data-modamai-route-seo="1"]')) {
      return undefined;
    }
    const path =
      canonicalPath != null && String(canonicalPath).trim()
        ? String(canonicalPath).trim()
        : location.pathname || '/';
    const href =
      origin && path
        ? `${origin}${path.startsWith('/') ? path : `/${path}`}`
        : '';

    if (!href) return undefined;

    let link = document.querySelector('link[rel="canonical"][data-modamai-seo="1"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('data-modamai-seo', '1');
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
    return () => {
      link?.remove();
    };
  }, [canonicalPath, location.pathname]);

  useEffect(() => {
    if (!noindex) return undefined;
    let el = document.querySelector('meta[name="robots"][data-modamai-seo="1"]');
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      el.setAttribute('data-modamai-seo', '1');
      document.head.appendChild(el);
    }
    el.setAttribute('content', 'noindex,nofollow');
    return () => {
      el?.remove();
    };
  }, [noindex]);

  return null;
}
